/**
 * PO Lines Filters Panel Component
 * CR-3413: Updated filters - removed Delivery From/To, combined status filters,
 * added searchable dropdowns for Customer and Supplier/Site.
 * Grouped into labelled sections (Order · Customer & Site · Part & Commodity ·
 * Status & Workflow · Dates) matching ScheduleFiltersPanel layout.
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Search, Calendar, X, ChevronDown } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { POLinesFilters, FilterOptions, STATUS_OPTIONS } from './types';
import { useView } from '../../../../contexts/ViewContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../../hooks/usePortalMode';

interface POLinesFiltersPanelProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

export function POLinesFiltersPanel({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: POLinesFiltersPanelProps) {
  // CR-3413: State for searchable dropdowns
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  // CR-4716: Customer portal users cannot see commercial/internal filters
  const { isCustomer: isCustomerUser } = usePortalMode();

  // When schedule_collaboration is enabled, hide collaboration-related filters
  // (managed at the schedule level instead).
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');

  // CR-4577: Filter suppliers by view context (group/division/site)
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  const { isCustomer } = usePortalMode();
  const viewScopedSuppliers = useMemo(() => {
    const all = filterOptions?.suppliers || [];
    // CR-5307: in customer-portal mode the supplier list is already scoped
    // by the backend (filter-options narrows to suppliers the customer
    // serves). selectedCompanyId is the CUSTOMER's id, never matches a
    // supplier id, so re-applying the supplier-side narrowing below would
    // collapse the list to empty and leave the Company Site dropdown blank.
    if (isCustomer) return all;
    if (viewLevel === 'SITE' && selectedCompanyId) return all.filter(c => c.id === selectedCompanyId);
    if (viewLevel === 'COMPANY' && selectedCompanyId) return all.filter(c => c.id === selectedCompanyId);
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = all.filter(c => c.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : all;
    }
    return all;
  }, [filterOptions?.suppliers, viewLevel, selectedCompanyId, selectedGroupId, isCustomer]);

  // CR-3413: Filtered customers based on search
  const filteredCustomers = useMemo(() => {
    const searchTerm = (filters.customerSearch || '').toLowerCase();
    if (!searchTerm) return filterOptions?.customers || [];
    return (filterOptions?.customers || []).filter(s =>
      s.name.toLowerCase().includes(searchTerm)
    );
  }, [filterOptions?.customers, filters.customerSearch]);

  // CR-3413: Filtered suppliers/sites based on search (scoped by view context)
  const filteredSuppliers = useMemo(() => {
    const searchTerm = (filters.supplierSearch || '').toLowerCase();
    if (!searchTerm) return viewScopedSuppliers;
    return viewScopedSuppliers.filter(c =>
      c.name.toLowerCase().includes(searchTerm)
    );
  }, [viewScopedSuppliers, filters.supplierSearch]);

  // CR-3413: Get display name for selected customer
  const selectedCustomerName = useMemo(() => {
    if (!filters.customer) return '';
    const customer = (filterOptions?.customers || []).find(s => s.id.toString() === filters.customer);
    return customer?.name || '';
  }, [filters.customer, filterOptions?.customers]);

  // CR-3413: Get display name for selected supplier/site
  const selectedSupplierName = useMemo(() => {
    if (!filters.supplier) return '';
    const supplier = (filterOptions?.suppliers || []).find(c => c.id.toString() === filters.supplier);
    return supplier?.name || '';
  }, [filters.supplier, filterOptions?.suppliers]);

  const inputCls = 'w-full h-8 px-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const dateInputCls = 'w-full h-8 pl-7 pr-2 text-sm text-foreground border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary';
  const labelCls = 'text-xs font-medium mb-1 block text-foreground leading-tight';
  const sectionTitleCls = 'text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5';
  const sectionCls = 'rounded-md border border-border/50 bg-muted/20 px-2.5 py-2';
  const gridCls = 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 gap-y-1.5';
  const compactGridCls = 'grid grid-cols-2 gap-x-2 gap-y-1.5';
  const selectCompactCls = '[&>button]:h-8 [&>button]:min-h-8 [&>button]:py-1 [&>button]:px-2 [&>button]:text-sm';

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4 border-b border-border/60">
        <CardTitle className="text-sm font-semibold">Filter SO Lines</CardTitle>
      </CardHeader>
      <CardContent className="px-4 py-3 space-y-2">
        <form onSubmit={(e) => { e.preventDefault(); onSearch(); }}>
        {/* Order + Dates — paired on wider screens to save vertical space */}
        <div className="grid md:grid-cols-2 gap-2">
          <div className={sectionCls}>
            <div className={sectionTitleCls}>Order</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>SO Number</label>
                <input
                  type="text"
                  placeholder="620596..."
                  value={filters.poNumber}
                  onChange={(e) => onFilterChange('poNumber', e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Line #</label>
                <input
                  type="text"
                  placeholder="1, 2, 3..."
                  value={filters.lineNumber}
                  onChange={(e) => onFilterChange('lineNumber', e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <div className={sectionCls}>
            <div className={sectionTitleCls}>Dates</div>
            <div className={compactGridCls}>
              <div>
                <label className={labelCls}>ODD From</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.oddFrom}
                    onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
              <div>
                <label className={labelCls}>ODD To</label>
                <div className="relative">
                  <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filters.oddTo}
                    onChange={(e) => onFilterChange('oddTo', e.target.value)}
                    className={dateInputCls}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer & site */}
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Customer &amp; Site</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-2 gap-y-1.5">
            <div className="relative">
              <label className={labelCls}>Customer</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={filters.customer ? selectedCustomerName : filters.customerSearch}
                  onChange={(e) => {
                    onFilterChange('customerSearch', e.target.value);
                    onFilterChange('customer', '');
                    setCustomerDropdownOpen(true);
                  }}
                  onFocus={() => setCustomerDropdownOpen(true)}
                  className={`${inputCls} pr-8`}
                />
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                {filters.customer && (
                  <button
                    type="button"
                    onClick={() => {
                      onFilterChange('customer', '');
                      onFilterChange('customerSearch', '');
                    }}
                    className="absolute right-7 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {customerDropdownOpen && filteredCustomers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredCustomers.slice(0, 50).map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        onFilterChange('customer', s.id.toString());
                        onFilterChange('customerSearch', '');
                        setCustomerDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              {customerDropdownOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setCustomerDropdownOpen(false)}
                />
              )}
            </div>

            <div>
              <label className={labelCls}>Customer Group</label>
              <SearchableSelect
                options={(filterOptions?.customer_groups || []).map((g) => ({ value: g.name, label: g.name }))}
                value={filters.customerGroup}
                onChange={(val) => onFilterChange('customerGroup', val)}
                placeholder="All Customer Groups"
                className={selectCompactCls}
              />
            </div>

            <div className="relative">
              <label className={labelCls}>Company Site</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search company sites..."
                  value={filters.supplier ? selectedSupplierName : filters.supplierSearch}
                  onChange={(e) => {
                    onFilterChange('supplierSearch', e.target.value);
                    onFilterChange('supplier', '');
                    setSupplierDropdownOpen(true);
                  }}
                  onFocus={() => setSupplierDropdownOpen(true)}
                  className={`${inputCls} pr-8`}
                />
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                {filters.supplier && (
                  <button
                    type="button"
                    onClick={() => {
                      onFilterChange('supplier', '');
                      onFilterChange('supplierSearch', '');
                    }}
                    className="absolute right-7 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {supplierDropdownOpen && filteredSuppliers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {filteredSuppliers.slice(0, 50).map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        onFilterChange('supplier', c.id.toString());
                        onFilterChange('supplierSearch', '');
                        setSupplierDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
              {supplierDropdownOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setSupplierDropdownOpen(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Part & commodity */}
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Part &amp; Commodity</div>
          <div className={gridCls}>
            <div>
              <label className={labelCls}>Commodity Group</label>
              <select
                value={filters.commodityGroup}
                onChange={(e) => {
                  onFilterChange('commodityGroup', e.target.value);
                  if (e.target.value !== filters.commodityGroup) {
                    onFilterChange('commodityType', '');
                  }
                }}
                className={inputCls}
              >
                <option value="">All Commodity Groups</option>
                {(filterOptions?.commodity_groups || []).map(cg => (
                  <option key={cg.id} value={cg.id}>{cg.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Commodity Type</label>
              <select
                value={filters.commodityType}
                onChange={(e) => onFilterChange('commodityType', e.target.value)}
                className={inputCls}
              >
                <option value="">All Commodity Types</option>
                {(filterOptions?.commodity_types || [])
                  .filter(ct => !filters.commodityGroup || ct.commodity_group_id === parseInt(filters.commodityGroup))
                  .map(ct => (
                    <option key={ct.id} value={ct.id}>{ct.name}</option>
                  ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>Part Number</label>
              <input
                type="text"
                placeholder="Part number..."
                value={filters.partNumber}
                onChange={(e) => onFilterChange('partNumber', e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Description</label>
              <input
                type="text"
                placeholder="Description..."
                value={filters.description}
                onChange={(e) => onFilterChange('description', e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Batch/Serial No</label>
              <input
                type="text"
                placeholder="Batch or serial..."
                value={filters.batchSerialNo}
                onChange={(e) => onFilterChange('batchSerialNo', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* Status & workflow */}
        <div className={sectionCls}>
          <div className={sectionTitleCls}>Status &amp; Workflow</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-1.5">
            <div>
              <label className={labelCls}>Status</label>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className={inputCls}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {!scheduleCollabEnabled && (
              <div>
                <label className={labelCls}>On Hold</label>
                <select
                  value={filters.on_hold}
                  onChange={(e) => onFilterChange('on_hold', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  <option value="yes">On Hold</option>
                  <option value="no">Not On Hold</option>
                </select>
              </div>
            )}

            <div>
              <label className={labelCls}>Next Action</label>
              <select
                value={filters.nextAction}
                onChange={(e) => onFilterChange('nextAction', e.target.value)}
                className={inputCls}
              >
                <option value="">All</option>
                <option value="supplier">Waiting Customer</option>
                <option value="customer">Waiting Supplier</option>
              </select>
            </div>

            {!isCustomerUser && (
              <div>
                <label className={labelCls}>OTIF Overridden</label>
                <select
                  value={filters.otifOverridden}
                  onChange={(e) => onFilterChange('otifOverridden', e.target.value)}
                  className={inputCls}
                >
                  <option value="">All</option>
                  <option value="yes">Overridden</option>
                  <option value="no">Not Overridden</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Search + Clear + Close Buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
          <Button type="submit" size="sm" className="px-5">
            <Search className="h-3.5 w-3.5 mr-1.5" />
            Search
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onClearFilters} className="px-5">
            <X className="h-3.5 w-3.5 mr-1.5" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button type="button" variant="outline" size="sm" onClick={onHideFilters}>
              Hide Filters
            </Button>
          )}
        </div>
        </form>
      </CardContent>
    </Card>
  );
}
